import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from '@/app/App';
import { TooltipProvider } from '@/components/ui/tooltip';
import { I18nProvider } from '@/i18n/I18nProvider';
import { queryClient } from '@/lib/queryClient';
import { registerPmtilesProtocol } from '@/lib/registerPmtilesProtocol';
import '@/index.css';
import 'maplibre-gl/dist/maplibre-gl.css';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
/** Иначе SDK ругается «Discarded session… missing release» и хуже группировка в Sentry. В CI задайте VITE_SENTRY_RELEASE (например git tag + sha). */
const sentryRelease =
  (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim() ||
  `sloy-web@${import.meta.env.MODE}`;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: sentryRelease,
    dist: (import.meta.env.VITE_SENTRY_DIST as string | undefined)?.trim() || undefined,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    debug: import.meta.env.DEV,
    // Трассировка: 10% запросов в prod; 100% в dev для отладки
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // Реплей только при ошибках — ловит OOM-краши
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });

  // Dev: консоль не видит ESM-импорты — экспорт для ручных проверок (captureException и т.д.)
  if (import.meta.env.DEV) {
    (window as Window & { Sentry?: typeof Sentry }).Sentry = Sentry;
  }
}

registerPmtilesProtocol();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BrowserRouter>
          <TooltipProvider>
            <App />
          </TooltipProvider>
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
